<script setup lang="ts">
import { createPopper, type Instance as PopperInstance } from '@popperjs/core';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { MrInput } from '#components';
import type { InputSizeType, TextAlignType, InputType } from '#imports';

const {
    dir = 'rtl',
    max = Infinity,
    maxLength = Infinity,
    maxPrice = Infinity,
    min = Infinity,
    minLength = 0,
    align = TextAlign.right,
    description = '',
    hideDescription,
    hideLabel,
    label = '',
    hideIndicator,
    clearable,
    icon,
    size = 'dense',
    inputId = '',
    disabled,
    invalid,
    fullWidth,
    mobileModal,
    pagingModalAnimation,
    placeholder = '',
    type = 'text',
    required,
    searchable,
    validation,
    readonly,
} = defineProps<IProps>();

const emit = defineEmits<{
    open: [];
    close: [];
    clear: [];
    submit: [];
    keydown: [event: KeyboardEvent];
    keypress: [event: KeyboardEvent];
}>();

interface IProps {
    hideDescription?: boolean;
    searchable?: boolean;
    size?: InputSizeType;
    label?: string;
    description?: string;
    disabled?: boolean;
    invalid?: boolean;
    validation?: (value: string | number | null) => boolean;
    icon?: IconDefinition;
    placeholder?: string;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    textArea?: boolean;
    resizable?: boolean;
    fullWidth?: boolean;
    required?: boolean;
    maxPrice?: number;
    type?: InputType;
    inputId?: string;
    dir?: 'rtl' | 'ltr';
    align?: TextAlignType;
    flatRight?: boolean;
    flatLeft?: boolean;
    hideIndicator?: boolean;
    mobileModal?: boolean;
    hideLabel?: boolean;
    pagingModalAnimation?: boolean;
    clearable?: boolean;
    scrollToContainer?: boolean;
    readonly?: boolean;
}

const root = ref<HTMLDivElement>();

const input = ref<InstanceType<typeof MrInput>>();
const dropdown = ref<HTMLDivElement>();
const modelValue = defineModel<string | number | null>({ default: '' });
const { isMobile } = useSize();

const opened = ref(false);
const focusedRightNow = ref(false);
const popper = ref<PopperInstance>();
const focus = () => {
    input.value?.focus();
    focusedRightNow.value = true;
};
const blur = () => input.value?.blur();
const open = () => {
    if (opened.value || readonly) {
        return;
    }
    opened.value = true;
    emit('open');
    nextTick(() => {
        focusedRightNow.value = false;
    });
    if (!isMobile.value && input.value && dropdown.value) {
        popper.value = createPopper(input.value?.$el, dropdown.value, {
            placement: 'bottom-end',
        });
    }
};
const close = () => {
    if (!opened.value || focusedRightNow.value) {
        return;
    }
    popper.value?.destroy();
    opened.value = false;
    emit('close');
};
onClickOutside(root, close);
const toggle = () => {
    opened.value = !opened.value;
};
onBeforeUnmount(() => {
    popper.value?.destroy();
});

defineExpose({
    opened,
    toggle,
    focus,
    blur,
    close,
    open,
});
</script>

<template>
    <div
        ref="root"
        class="mr-select-input-container"
        :class="{
            'mobile-modal': mobileModal,
            'paging-animation': pagingModalAnimation,
            'full-width': fullWidth,
        }"
    >
        <mr-input
            ref="input"
            v-model="modelValue"
            class="select-input"
            :label="hideLabel ? '' : label"
            :readonly="!searchable"
            autocomplete="off"
            :clearable
            :hide-description
            :align
            :description
            :dir
            :disabled
            :flat-left
            :flat-right
            :full-width
            :icon
            :input-id
            :invalid
            :max
            :max-length
            :max-price
            :min
            :min-length
            :placeholder
            :required
            :resizable
            :size
            :text-area
            :type
            :validation
            :scroll-to-container
            bypass-i-m-e-composition
            @clear="emit('clear')"
            @focus="open"
            @submit="emit('submit')"
            @search="modelValue = $event"
            @keypress="emit('keypress', $event)"
            @keydown="emit('keydown', $event)"
        >
            <mr-icon
                v-if="!hideIndicator"
                class="indicator-icon"
                :class="{ opened }"
                :icon="faAngleDown"
                size="large"
                @click.stop
            />
            <slot name="selected" />
        </mr-input>
        <ClientOnly>
            <Transition>
                <div
                    v-show="opened"
                    ref="dropdown"
                    class="dropdown-container"
                    :class="{ 'width-description': !hideDescription }"
                >
                    <slot name="header" />
                    <slot />
                    <slot name="footer" />
                </div>
            </Transition>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
.mr-select-input-container {
    position: relative;
    .select-input :deep(.input-wrapper) {
        position: relative;
    }
    &.full-width {
        width: 100%;
    }
}

.indicator-icon {
    transition: transform 0.2s linear;
    margin-left: 8px;
    &.opened {
        transform: rotate(-180deg);
    }
}

.dropdown-container {
    @include shadow;
    position: absolute;
    z-index: 1001000;
    background: white;
    border-radius: 12px;
    margin-top: 4px;
    min-width: 100%;
    &.width-description {
        margin-top: -25px;
    }
}

@media (max-width: $app-mobile-max-width) {
    .mr-select-input-container {
        &.mobile-modal {
            .dropdown-container {
                display: flex;
                flex-direction: column;
                position: fixed;
                top: 0;
                right: 0;
                width: 100%;
                height: 100%;
                border-radius: 0;
                padding: 0;
                margin-top: 0;
            }
            &.paging-animation {
                @include v-transition-horizontal-paging;
            }
            &:not(.paging-animation) {
                @include v-transition-scale-3d;
            }
        }
    }
}
</style>
